/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Chaitanya
*/

import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Toast from 'react-native-toast-message';
import {
  View,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  KeyboardAvoidingView,
} from 'react-native';
import SociollaText from '@components/sociolla/SociollaText';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import Input from '@components/form/Input';
import DatePicker from '@components/form/DatePicker';
import { addDays } from 'date-fns';
import SelectBox from '@components/form/SelectBox';
import CustomerBrandBudget from './components/SelectCustomerBrandBudget';
import AttachmentsInput from '@components/form/AttachmentsInput';
import RenderIf from '@components/global/RenderIf';
import ModalUpload from '@components/modal/ModalUpload';

import * as Layout from '@components/screen/Layout';
import { isWeb, isMobileWeb, isAndroid, fullHeight } from '@global/screens';
import { showPopup } from '@/store/modal';
import { BOOLEAN_STATUS } from '@/constants';
import useApi from '@/hooks/useApi';
import { MsOrder } from '@api/ms-order';
import { MsB2b } from '@api/ms-b2b';
import { showAlert } from '@/store/global';
import { uploadS3, formatCoaTaxLabel } from '@global/helpers';
import templateSubdis from '@/assets/csv/template_import_skp_internal_subdis';
import templateNonSubdis from '@/assets/csv/template_import_skp_internal_non_subdis';
import templateSubdisAutofill from '@/assets/csv/template_import_skp_internal_subdis_autofill';
import templateNonSubdisAutofill from '@/assets/csv/template_import_skp_internal_non_subdis_autofill';

const FormValidationSchema = Yup.object().shape({
  skp_name: Yup.string().required('skp name is required.'),
  from_date: Yup.string().required('from date is required.'),
  to_date: Yup.string().required('to date is required.'),
  description: Yup.string().required('description is required.'),
  is_active: Yup.boolean(),
  coa: Yup.object().required('COA is required.'),
  tax_corporate: Yup.object().required('Tax ID Corporate is required.'),
  tax_personal: Yup.object().required('Tax ID Personal is required.'),
  total_estimate_budget: Yup.number().required(
    'total estimate budget is required.',
  ),
  notes: Yup.string(),
  attachment: Yup.array(),
  skp_type: Yup.string().required('Type is required.'),
});

const SKP_TYPE = [
  {
    value: 'mt',
    label: 'MT',
  },
  {
    value: 'gt',
    label: 'GT',
  },
  {
    value: 'subdistributor',
    label: 'Sub Distributor',
  },
];

const SkpInternalFormScreen = (props) => {
  const formRef = useRef();
  const scrollRef = useRef();
  const dispatch = useDispatch();
  const showOptions = useSelector(
    (state) => state.modal.listCustomerAndBrandOptions,
  );
  const showAddManual = useSelector((state) => state.modal.showAddManual);
  const showImportModalSkp = useSelector(
    (state) => state.modal.showImportModalSkp,
  );

  const [detailData, setDetailData] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [coaTaxes, setCoaTaxes] = useState([]);
  const [coaTaxesLoading, setCoaTaxesLoading] = useState(false);
  const [coaTaxesParams, setCoaTaxesParams] = useState({
    skip: 0,
    limit: 10,
    loading: false,
  });
  const [customerBrandBudgetTableCount, setCustomerBrandBudgetTableCount] =
    useState(0);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [csvMode, setCsvMode] = useState('manual');
  const csvModeRef = useRef('manual');
  const [isReady, setIsReady] = useState(false);
  const [taxCorporates, setTaxCorporates] = useState([]);
  const [taxPersonals, setTaxPersonals] = useState([]);

  const getSkpInternalDetail = useApi(MsOrder.getSkpInternalDetail);
  const getCoaTaxSettings = useApi(MsOrder.getCoaTaxSettings);
  const getCoaTaxSettingsDetail = useApi(MsOrder.getCoaTaxSettingsDetail);
  const searchCoaTaxSettings = useApi(MsB2b.getCoaTaxes);
  const createSkpInternalsBrandBudget = useApi(
    MsOrder.createSkpInternalsBrandBudget,
  );
  const getSkpInternalsBrandBudgetCount = useApi(
    MsOrder.getSkpInternalsBrandBudgetCount,
  );

  const getDetail = async (id) => {
    const response = await getSkpInternalDetail.request(id);
    const customerBrandBudgetCount =
      await getSkpInternalsBrandBudgetCount.request({
        params: { filter: { 'skp_internal.id': id } },
      });
    if (response?.data?.data && Object.keys(response.data.data).length) {
      const result = response.data.data;
      result.id = result._id;

      if (result.coa_tax?.id) {
        const skpInternalCoaTax = await getCoaTaxSettingsDetail.request(
          result.coa_tax.id,
        );
        if (
          skpInternalCoaTax?.data?.data &&
          Object.keys(skpInternalCoaTax?.data?.data).length
        ) {
          const coaTaxData = filterDeletedTax(skpInternalCoaTax.data.data);
          onChangeTax(coaTaxData);

          // Handle old skp data
          if (
            !result.coa_tax.tax_corporate ||
            !result.coa_tax.tax_personal ||
            !result.coa_tax.non_tax_corporate ||
            !result.coa_tax.non_tax_personal
          ) {
            result.coa_tax = {
              ...result.coa_tax,
              tax_corporate: (coaTaxData.tax_corporate || []).find(
                (t) => t.is_default,
              ),
              tax_personal: (coaTaxData.tax_personal || []).find(
                (t) => t.is_default,
              ),
              non_tax_corporate: (coaTaxData.non_tax_corporate || []).find(
                (t) => t.is_default,
              ),
              non_tax_personal: (coaTaxData.non_tax_personal || []).find(
                (t) => t.is_default,
              ),
            };
          }
        }
      }

      const mt = result.market_type?.code || '';
      result.skp_type = result.skp_type || mt.toLowerCase();

      setDetailData(result);

      if (
        customerBrandBudgetCount?.data?.data ||
        customerBrandBudgetCount?.data?.data == 0
      ) {
        setCustomerBrandBudgetTableCount(customerBrandBudgetCount.data.data);
      }
      setIsReady(true);
    }
  };

  const onChangeTax = (coa) => {
    if (coa.tax_corporate?.length) {
      setTaxCorporates(coa.tax_corporate);
    }
    if (coa.tax_personal?.length) {
      setTaxPersonals(coa.tax_personal);
    }
  };

  const filterDeletedTax = (coa) => {
    return {
      ...coa,
      tax_corporate: (coa.tax_corporate || [])
        .filter((t) => !t.is_deleted)
        .map((t) => ({
          ...t,
          id: t.id || t._id,
          label: formatCoaTaxLabel(t),
        })),
      tax_personal: (coa.tax_personal || [])
        .filter((t) => !t.is_deleted)
        .map((t) => ({
          ...t,
          id: t.id || t._id,
          label: formatCoaTaxLabel(t),
        })),
      non_tax_corporate: (coa.non_tax_corporate || [])
        .filter((t) => !t.is_deleted)
        .map((t) => ({
          ...t,
          id: t.id || t._id,
          label: formatCoaTaxLabel(t),
        })),
      non_tax_personal: (coa.non_tax_personal || [])
        .filter((t) => !t.is_deleted)
        .map((t) => ({
          ...t,
          id: t.id || t._id,
          label: formatCoaTaxLabel(t),
        })),
    };
  };

  const getCoaTaxes = async ({ val = null, skip = 0 }) => {
    if (skip == 0) {
      setCoaTaxesLoading(true);
    }
    setCoaTaxesParams({ ...coaTaxesParams, loading: skip > 0, skip: skip });
    const payload = {
      params: {
        filter: {
          is_active: true,
        },
        skip: skip,
        limit: coaTaxesParams.limit,
      },
    };

    if (val) {
      payload.params.filter.keyword = val;
    }
    let response;
    if (val) {
      response = await searchCoaTaxSettings.request(payload);
    } else {
      response = await getCoaTaxSettings.request(payload);
    }
    if (response.status == 200 && response?.data?.data?.length) {
      const old_result = JSON.parse(JSON.stringify(coaTaxes));
      const result = response.data.data.map((item) => {
        if (item._id) {
          item.id = item._id;
        }
        return item;
      });
      let finalResult = result;
      if (skip > 0) {
        finalResult = old_result.concat(result);
      }
      finalResult = finalResult.map((fr) => filterDeletedTax(fr));
      setCoaTaxes(finalResult);
      if (skip > 0) {
        setCoaTaxesParams({ ...coaTaxesParams, loading: false, skip: skip });
      }
    } else {
      if (skip <= 0) {
        setCoaTaxes([]);
      }
      setCoaTaxesParams({ ...coaTaxesParams, loading: false, skip: skip });
    }
    setCoaTaxesLoading(false);
  };

  const submitForm = async (payload, isNavigateToForm = false) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      const finalPayload = {
        name: payload.skp_name,
        description: payload.description,
        coa_tax: {
          ...payload.coa,
          tax_corporate: {
            ...payload.tax_corporate,
            id: payload.tax_corporate.id || payload.tax_corporate._id,
          },
          tax_personal: {
            ...payload.tax_personal,
            id: payload.tax_personal.id || payload.tax_personal._id,
          },
          non_tax_corporate: {
            ...payload.non_tax_corporate,
            id: payload.non_tax_corporate.id || payload.non_tax_corporate._id,
          },
          non_tax_personal: {
            ...payload.non_tax_personal,
            id: payload.non_tax_personal.id || payload.non_tax_personal._id,
          },
        },
        is_active: payload.is_active || false,
        from_date: payload.from_date,
        to_date: payload.to_date,
        market_type: payload.market_type,
        total_estimate_budget: Math.round(
          Number(payload.total_estimate_budget),
        ),
        status: payload.status || 'draft',
        notes: payload.notes,
        attachments: payload?.attachment || [],
        skp_type: payload.skp_type?.toLowerCase(),
      };

      if (!isEdit) {
        const createData = await MsOrder.createSkpInternal(finalPayload);
        if (createData?.data?.data) {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'Successfully added data',
            type: 'success',
          });
          if (!isNavigateToForm) {
            props.navigation.navigate('SkpInternalDetailScreen', {
              id: createData?.data?.data?._id,
              name: createData?.data?.data?.name,
              titleScreen: createData?.data?.data?.name,
            });
          } else {
            return createData.data.data;
          }
        } else {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'A system error occurred',
            type: 'error',
          });
        }
      } else {
        const updateData = await MsOrder.updateSkpInternal(
          detailData._id,
          finalPayload,
        );
        if (updateData?.data?.data) {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'Successfully edited data',
            type: 'success',
          });
          if (!isNavigateToForm) {
            props.navigation.navigate('SkpInternalDetailScreen', {
              id: updateData?.data?.data?._id,
              name: updateData?.data?.data?.name,
              titleScreen: updateData?.data?.data?.name,
            });
          } else {
            return updateData.data.data;
          }
        } else {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'A system error occurred',
            type: 'error',
          });
        }
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (error) {
      console.log(error);
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: error?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
    }
  };

  const onProcessBulkUpload = async (id = '', file = null) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      const url = await uploadS3({
        file: file || bulkUploadFile,
        isNeedMobileSupport: true,
      });
      setBulkUploadFile(null);
      if (url) {
        const payload = {
          url,
          csv_mode: csvModeRef.current,
        };
        await MsOrder.uploadListCustomerBrandBudget(id, payload);
        dispatch(
          showPopup({
            name: 'showImportModalSkp',
            value: false,
          }),
        );
        dispatch(
          showAlert({
            show: true,
            title: 'Information',
            description: 'List Customer And Brand Budget Has Been Added',
            isConfirm: false,
            onCallback: () => {
              getDetail(id);
            },
          }),
        );
      } else {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description: 'Failed to upload file. Please try again.',
            isConfirm: false,
          }),
        );
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (err) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  const saveCustomerBrandBudgets = async (payload, skp) => {
    if (!skp?._id) {
      Toast.show({
        text1: 'SKP Internal not found',
        type: 'error',
      });
      return;
    }

    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      payload['skp_internal'] = {
        id: skp?._id,
        name: skp?.name,
        coa_tax_id: skp?.coa_tax?.id,
      };
      const result = await createSkpInternalsBrandBudget.request(payload);
      if (result?.data?.data) {
        Toast.show({
          text1: 'Customer and Brand Budget saved successfully',
          type: 'success',
        });
      } else {
        Toast.show({
          text1: 'Customer and Brand Budget not saved',
          type: 'error',
        });
      }
    } catch (error) {
      console.log(error);
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: error?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
    } finally {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      props.navigation.navigate('SkpInternalDetailScreen', {
        id: skp._id,
        name: skp?.name,
        titleScreen: skp?.name,
      });
    }
  };

  const handleUpload = async (file) => {
    setBulkUploadFile(file || {});
    if (detailData?._id) {
      onProcessBulkUpload(detailData?._id, file || {});
    } else {
      if (formRef.current) {
        const res = await submitForm(formRef.current.values, true);
        if (res?._id) {
          await onProcessBulkUpload(res?._id, file || {});
          props.navigation.replace('SkpInternalDetailScreen', {
            id: res._id,
            name: res?.name,
            titleScreen: res?.name,
          });
        }
      }
    }
  };

  const goToForm = async (type, mode = 'manual') => {
    closeOptions();
    if (type == 'add') {
      dispatch(showPopup({ name: 'showAddManual', value: true }));
    } else {
      setCsvMode(mode);
      csvModeRef.current = mode;
      dispatch(showPopup({ name: 'showImportModalSkp', value: true }));
    }
  };

  const closeOptions = () => {
    dispatch(showPopup({ name: 'listCustomerAndBrandOptions', value: false }));
  };

  const goBack = () => {
    // replace-self resets the form state so nothing gets stuck on re-entry
    props.navigation.replace('SkpInternalFormScreen', {});
    // edit → back to the detail it came from; create → back to the list
    if (isEdit && detailData?._id) {
      props.navigation.navigate('SkpInternalDetailScreen', {
        id: detailData._id,
        name: detailData?.name,
        titleScreen: detailData?.name,
      });
    } else {
      props.navigation.navigate('SkpInternalScreen');
    }
  };

  const filterCustomerBySkpType = (skpType) => {
    if (!skpType) {
      return {};
    }
    if (skpType.toLowerCase() == 'subdistributor') {
      return {
        is_sub_distributor: true,
      };
    } else {
      return {
        'market_type.code': {
          $in: [skpType.toUpperCase(), skpType.toLowerCase()],
        },
      };
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setIsEdit(false);
      setDetailData({});
      setIsReady(false);
      if (props?.route?.params?.id) {
        setIsEdit(true);
        getDetail(props.route.params.id);
      } else {
        setTimeout(() => {
          setIsReady(true);
        }, 500);
      }
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  return (
    <Layout.Container testID="skp-internal-form-screen">
      {(isEdit && getSkpInternalDetail?.loading) || !isReady ? (
        <Layout.Wrapper>
          <Layout.Loader />
        </Layout.Wrapper>
      ) : (isEdit ? detailData && Object.keys(detailData).length : true) ? (
        <Formik
          innerRef={formRef}
          initialValues={{
            skp_name: isEdit && detailData?.name ? detailData.name : '',
            description:
              isEdit && detailData?.description ? detailData.description : '',
            from_date:
              isEdit && detailData?.from_date ? detailData.from_date : '',
            to_date: isEdit && detailData?.to_date ? detailData.to_date : '',
            coa: isEdit && detailData?.coa_tax ? detailData.coa_tax : '',
            tax_corporate:
              isEdit && detailData?.coa_tax?.tax_corporate
                ? detailData.coa_tax.tax_corporate
                : '',
            tax_personal:
              isEdit && detailData?.coa_tax?.tax_personal
                ? detailData.coa_tax.tax_personal
                : '',
            non_tax_corporate:
              isEdit && detailData?.coa_tax?.non_tax_corporate
                ? detailData.coa_tax.non_tax_corporate
                : '',
            non_tax_personal:
              isEdit && detailData?.coa_tax?.non_tax_personal
                ? detailData.coa_tax.non_tax_personal
                : '',
            is_active:
              isEdit && detailData?.is_active ? detailData.is_active : false,
            market_type:
              isEdit && detailData?.market_type ? detailData.market_type : '',
            total_estimate_budget:
              isEdit && detailData?.total_estimate_budget
                ? detailData.total_estimate_budget
                : '',
            status: isEdit && detailData?.status ? detailData.status : '',
            notes: isEdit && detailData?.notes ? detailData.notes : '',
            attachment:
              isEdit && detailData?.attachment ? detailData.attachment : [],
            skp_type: isEdit && detailData?.skp_type ? detailData.skp_type : '',
          }}
          validationSchema={FormValidationSchema}
          onSubmit={async (payload) => submitForm(payload)}>
          {({
            setFieldValue,
            validateForm,
            handleSubmit,
            handleBlur,
            errors,
            touched,
            values,
            setTouched,
          }) => (
            <>
              {isWeb ? (
                <Layout.Wrapper white={false}>
                  <Layout.Back
                    testID="skp-internal-form-back"
                    title="SKP Internal"
                    onCallback={() => goBack()}
                  />
                  <Layout.Title
                    testID="skp-internal-form-title"
                    title={`${isEdit ? `Edit` : `Add`} SKP Internal`}>
                    <View style={styles.row}>
                      {!isEdit || detailData.status == 'draft' ? (
                        <Button
                          testID="skp-internal-form-request-confirm-btn"
                          type="primary"
                          rounded
                          disabled={!customerBrandBudgetTableCount}
                          disabledStyle={{
                            width: 185,
                            marginRight: 10,
                          }}
                          buttonStyle={{
                            width: 185,
                            backgroundColor: Colors.blue0,
                            marginRight: 10,
                          }}
                          title={
                            <SociollaText
                              type="bold"
                              style={{
                                fontSize: 14,
                                letterSpacing: 0.5,
                                color: Colors.white,
                              }}>
                              Request Confirmation
                            </SociollaText>
                          }
                          onPress={(evt) => {
                            if (!values.attachment?.length) {
                              Toast.show({
                                text1: 'Attachment is required',
                                type: 'error',
                              });
                              return;
                            }
                            setFieldValue('status', 'need_approval');
                            handleSubmit(evt);
                          }}
                        />
                      ) : (
                        false
                      )}
                      <Button
                        testID="skp-internal-form-save-btn"
                        type="primary"
                        rounded
                        buttonStyle={{ width: 120 }}
                        title={
                          <SociollaText
                            type="bold"
                            style={{
                              fontSize: 14,
                              letterSpacing: 0.5,
                              color: Colors.white,
                            }}>
                            {isEdit ? 'SAVE' : 'Save Draft'}
                          </SociollaText>
                        }
                        onPress={(evt) => {
                          setFieldValue('status', 'draft');
                          handleSubmit(evt);
                        }}
                      />
                    </View>
                  </Layout.Title>
                </Layout.Wrapper>
              ) : (
                false
              )}
              <Layout.Wrapper rounded>
                <KeyboardAvoidingView
                  style={{ flex: 1 }}
                  keyboardVerticalOffset={0}>
                  <ScrollView
                    style={
                      !isWeb ? { height: fullHeight } : { overflow: 'visible' }
                    }
                    ref={scrollRef}
                    contentContainerStyle={{ width: '100%' }}>
                    <Layout.FormWrapper
                      style={{
                        paddingHorizontal: 15,
                        paddingTop: 20,
                        ...(isWeb
                          ? {
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              paddingHorizontal: 20,
                              marginBottom: 15,
                            }
                          : {
                              paddingBottom: !isMobileWeb
                                ? isAndroid
                                  ? 200
                                  : 150
                                : 120,
                            }),
                      }}>
                      <View style={styles.rowForm}>
                        <Layout.FormGroup>
                          <Input
                            testID="skp-internal-form-skp-name"
                            label="SKP Name"
                            value={values.skp_name}
                            isRequired
                            placeholder="SKP Name"
                            hasError={
                              errors.skp_name && touched.skp_name
                                ? errors.skp_name
                                : null
                            }
                            onChangeText={(value) =>
                              setFieldValue('skp_name', value)
                            }
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <Input
                            testID="skp-internal-form-description"
                            label="Description"
                            value={values.description}
                            multiline={true}
                            numberOfLines={4}
                            isRequired
                            placeholder="Description"
                            hasError={
                              errors.description && touched.description
                                ? errors.description
                                : null
                            }
                            onChangeText={(value) =>
                              setFieldValue('description', value)
                            }
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <SelectBox
                            testID="skp-internal-form-coa"
                            isRequired={true}
                            label={'COA'}
                            options={coaTaxes}
                            value={values.coa}
                            placeholder="Select COA"
                            trackBy="id"
                            labelKey="cost_type"
                            formLabelKey="cost_type"
                            loading={coaTaxesLoading}
                            withCallback={true}
                            withLoadMore
                            limit={coaTaxesParams.limit}
                            loadMoreEmpty={
                              coaTaxesParams.skip > coaTaxes?.length
                            }
                            loadingCallback={coaTaxesParams.loading}
                            onLoadMore={(keyword) => {
                              getCoaTaxes({
                                val: keyword,
                                skip:
                                  coaTaxesParams.skip + coaTaxesParams.limit,
                              });
                            }}
                            searchCallback={(keyword) => {
                              getCoaTaxes({
                                val: keyword,
                              });
                            }}
                            onReady={() => {
                              getCoaTaxes({});
                            }}
                            hasError={
                              errors.coa && touched.coa ? errors.coa : null
                            }
                            onChange={(value) => {
                              setFieldValue('coa', value);
                              onChangeTax(value);

                              if (!isEdit) {
                                const taxCorporate = (
                                  value.tax_corporate || []
                                ).find((t) => t.is_default);
                                if (taxCorporate) {
                                  setFieldValue('tax_corporate', taxCorporate);
                                }
                                const taxPersonal = (
                                  value.tax_personal || []
                                ).find((t) => t.is_default);
                                if (taxPersonal) {
                                  setFieldValue('tax_personal', taxPersonal);
                                }
                                const nonTaxCorporate = (
                                  value.non_tax_corporate || []
                                ).find((t) => t.is_default);
                                if (nonTaxCorporate) {
                                  setFieldValue(
                                    'non_tax_corporate',
                                    nonTaxCorporate,
                                  );
                                }
                                const nonTaxPersonal = (
                                  value.non_tax_personal || []
                                ).find((t) => t.is_default);
                                if (nonTaxPersonal) {
                                  setFieldValue(
                                    'non_tax_personal',
                                    nonTaxPersonal,
                                  );
                                }
                              }
                            }}
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <SelectBox
                            testID="skp-internal-form-tax-corporate"
                            isRequired={true}
                            label={'Tax ID Corporate'}
                            options={taxCorporates}
                            value={values.tax_corporate}
                            placeholder="Select Tax ID Corporate"
                            trackBy="id"
                            labelKey="label"
                            formLabelKey="label"
                            hasError={
                              errors.tax_corporate && touched.tax_corporate
                                ? errors.tax_corporate
                                : null
                            }
                            onChange={(value) =>
                              setFieldValue('tax_corporate', value)
                            }
                            editable={!!values.coa}
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <SelectBox
                            testID="skp-internal-form-tax-personal"
                            isRequired={true}
                            label={'Tax ID Personal'}
                            options={taxPersonals}
                            value={values.tax_personal}
                            placeholder="Select Tax ID Corporate"
                            trackBy="id"
                            labelKey="label"
                            formLabelKey="label"
                            hasError={
                              errors.tax_personal && touched.tax_personal
                                ? errors.tax_personal
                                : null
                            }
                            onChange={(value) =>
                              setFieldValue('tax_personal', value)
                            }
                            editable={!!values.coa}
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <SelectBox
                            testID="skp-internal-form-type"
                            isRequired={true}
                            label={'Type'}
                            options={SKP_TYPE}
                            value={values.skp_type}
                            placeholder="Select Type"
                            trackBy="value"
                            labelKey="label"
                            formLabelKey="label"
                            hasError={
                              errors.skp_type && touched.skp_type
                                ? errors.skp_type
                                : null
                            }
                            onChange={(value) =>
                              setFieldValue('skp_type', value.value)
                            }
                            editable={!customerBrandBudgetTableCount}
                          />
                        </Layout.FormGroup>
                      </View>
                      <View style={styles.rowForm}>
                        <Layout.FormGroup>
                          <SociollaText style={{ fontSize: 11 }}>
                            Periode
                          </SociollaText>
                          <View
                            style={{
                              width: '100%',
                              flexDirection: 'row',
                              gap: 10,
                            }}>
                            <DatePicker
                              testID="skp-internal-form-period-from-date"
                              label="From Date"
                              name="from_date"
                              style={{
                                marginRight: isWeb ? 10 : 0,
                                width: isWeb ? 'calc(50% - 5px)' : '49%',
                              }}
                              value={values.from_date}
                              // disablePastDates={true}
                              hasError={
                                errors.from_date && touched.from_date
                                  ? errors.from_date
                                  : null
                              }
                              onChange={(value) => {
                                setFieldValue(
                                  'to_date',
                                  addDays(new Date(value), 1),
                                );
                                setFieldValue('from_date', value);
                              }}
                            />
                            <DatePicker
                              testID="skp-internal-form-period-to-date"
                              label="To Date"
                              name="to_date"
                              style={{
                                width: isWeb ? 'calc(50% - 5px)' : '49%',
                              }}
                              value={values.to_date}
                              // disablePastDates={true}
                              disableBeforeDate={values.from_date}
                              hasError={
                                errors.to_date && touched.to_date
                                  ? errors.to_date
                                  : null
                              }
                              onChange={(value) =>
                                setFieldValue('to_date', value)
                              }
                            />
                          </View>
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <SelectBox
                            testID="skp-internal-form-is-active"
                            label="Is Active"
                            options={BOOLEAN_STATUS}
                            value={
                              values.is_active
                                ? BOOLEAN_STATUS[0]
                                : BOOLEAN_STATUS[1]
                            }
                            placeholder="Select is active"
                            trackBy="value"
                            labelKey="name"
                            isRequired
                            hasError={
                              errors.is_active && touched.is_active
                                ? errors.is_active
                                : null
                            }
                            onChange={(value) => {
                              setFieldValue('is_active', value.value);
                            }}
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <Input
                            testID="skp-internal-form-estimate-budget"
                            label="Total Estimate Budget"
                            value={values.total_estimate_budget}
                            isRequired
                            placeholder="Total Estimate Budget"
                            hasError={
                              errors.total_estimate_budget &&
                              touched.total_estimate_budget
                                ? errors.total_estimate_budget
                                : null
                            }
                            onChangeText={(value) =>
                              setFieldValue('total_estimate_budget', value)
                            }
                            onBlur={handleBlur('total_estimate_budget')}
                            onFocus={() => {
                              setTimeout(() => {
                                scrollRef.current.scrollTo({
                                  y: 200,
                                  animated: true,
                                });
                              }, 300);
                            }}
                            keyboardType="numeric"
                            isNumeric={true}
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup style={{ zIndex: 2 }}>
                          <SociollaText style={{ fontSize: 11 }}>
                            List Customer And Brand
                          </SociollaText>
                          <View style={{ flexDirection: 'row' }}>
                            <RenderIf condition={detailData?.is_csv_processing}>
                              <SociollaText style={{ marginTop: 5 }}>
                                Processing...
                              </SociollaText>
                            </RenderIf>
                            <RenderIf
                              condition={!detailData?.is_csv_processing}>
                              {customerBrandBudgetTableCount &&
                              detailData?._id ? (
                                <Button
                                  testID="skp-internal-form-view-budget-btn"
                                  type="primary"
                                  rounded={true}
                                  buttonStyle={{
                                    width: 100,
                                    backgroundColor: Colors.white,
                                    marginTop: 10,
                                  }}
                                  title={
                                    <SociollaText
                                      type="medium"
                                      style={{
                                        fontSize: 14,
                                        color: Colors.black,
                                      }}>
                                      View
                                    </SociollaText>
                                  }
                                  onPress={() =>
                                    props.navigation.navigate(
                                      'SkpInternlBudgetTableScreen',
                                      { id: detailData._id },
                                    )
                                  }
                                />
                              ) : (
                                <Button
                                  testID="skp-internal-form-add-budget-btn"
                                  type="primary"
                                  rounded={true}
                                  disabled={!values.skp_type}
                                  disabledStyle={{
                                    width: 100,
                                  }}
                                  buttonStyle={{
                                    width: 100,
                                    backgroundColor: Colors.white,
                                    marginTop: 10,
                                  }}
                                  title={
                                    <SociollaText
                                      type="medium"
                                      style={{
                                        fontSize: 14,
                                        color: Colors.black,
                                      }}>
                                      + Add
                                    </SociollaText>
                                  }
                                  onPress={() =>
                                    dispatch(
                                      showPopup({
                                        name: 'listCustomerAndBrandOptions',
                                        value: !showOptions,
                                      }),
                                    )
                                  }
                                />
                              )}
                            </RenderIf>
                            {showOptions ? (
                              <View style={styles.addOptions}>
                                <TouchableOpacity
                                  testID="skp-internal-form-add-budget-add-manually-btn"
                                  activeOpacity={0.8}
                                  style={styles.addOptionsItem}
                                  onPress={() => goToForm('add')}>
                                  <SociollaText>Add Manually</SociollaText>
                                </TouchableOpacity>
                                <TouchableOpacity
                                  testID="skp-internal-form-add-budget-import-csv-btn"
                                  activeOpacity={0.8}
                                  style={styles.addOptionsItem}
                                  onPress={() => goToForm('csv', 'manual')}>
                                  <SociollaText>Import CSV</SociollaText>
                                </TouchableOpacity>
                                <TouchableOpacity
                                  testID="skp-internal-form-add-budget-import-csv-autofill-btn"
                                  activeOpacity={0.8}
                                  style={styles.addOptionsItem}
                                  onPress={() => goToForm('csv', 'autofill')}>
                                  <SociollaText>
                                    Import CSV (Auto-fill)
                                  </SociollaText>
                                </TouchableOpacity>
                              </View>
                            ) : (
                              false
                            )}
                          </View>
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <Input
                            testID="skp-internal-form-notes"
                            label="Notes"
                            value={values.notes}
                            multiline={true}
                            numberOfLines={4}
                            placeholder="Notes"
                            hasError={
                              errors.notes && touched.notes
                                ? errors.notes
                                : null
                            }
                            onChangeText={(value) =>
                              setFieldValue('notes', value)
                            }
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <AttachmentsInput
                            testID="skp-internal-form-attachment"
                            label="Upload Attachments"
                            value={values.attachment}
                            isRequired
                            hasError={
                              errors.attachment && touched.attachment
                                ? errors.attachment
                                : null
                            }
                            onChange={(updatedAttachments) =>
                              setFieldValue('attachment', updatedAttachments)
                            }
                          />
                        </Layout.FormGroup>
                        {!isWeb && (
                          <View
                            style={{
                              height:
                                values?.attachment?.length > 1
                                  ? 40 * values?.attachment?.length
                                  : 250,
                            }}></View>
                        )}
                      </View>
                    </Layout.FormWrapper>
                  </ScrollView>
                </KeyboardAvoidingView>
              </Layout.Wrapper>
              <CustomerBrandBudget
                testID="skp-internal-form-customer-brand-budget-modal"
                customerType={values.skp_type}
                customerFilter={filterCustomerBySkpType(values.skp_type)}
                show={showAddManual}
                onChange={(val) =>
                  validateForm()
                    .then((errors) => {
                      if (Object.keys(errors).length === 0) {
                        return submitForm(values, true);
                      } else {
                        setTouched({ ...errors, ...touched });
                      }
                    })
                    .then((result) => {
                      return saveCustomerBrandBudgets(val, result);
                    })
                }
                onClose={() =>
                  dispatch(showPopup({ name: 'showAddManual', value: false }))
                }
              />
              {!isWeb && (isEdit ? !getSkpInternalDetail?.loading : true) ? (
                <Layout.ButtonSticky
                  testID="skp-internal-form-sticky-buttons"
                  show={props?.route?.name == 'SkpInternalFormScreen'}
                  route={props?.route}
                  buttons={[
                    {
                      title: 'Request Confirmation',
                      disabled: !customerBrandBudgetTableCount,
                      onPress: (evt) => {
                        if (!values.attachment?.length) {
                          Toast.show({
                            text1: 'Attachment is required',
                            type: 'error',
                          });
                          return;
                        }
                        setFieldValue('status', 'need_approval');
                        validateForm().then(() => {
                          handleSubmit(evt);
                        });
                      },
                    },
                    {
                      title: 'Save Draft',
                      type: 'primary',
                      onPress: (evt) => {
                        setFieldValue('status', 'draft');
                        handleSubmit(evt);
                      },
                    },
                  ]}
                />
              ) : (
                false
              )}
              <ModalUpload
                testID="skp-internal-form-import-budget-modal"
                show={showImportModalSkp}
                onClose={() =>
                  dispatch(
                    showPopup({
                      name: 'showImportModalSkp',
                      value: false,
                    }),
                  )
                }
                templateUrl={
                  values.skp_type?.toLowerCase() == 'subdistributor'
                    ? csvMode === 'autofill'
                      ? templateSubdisAutofill
                      : templateSubdis
                    : csvMode === 'autofill'
                    ? templateNonSubdisAutofill
                    : templateNonSubdis
                }
                onUpload={handleUpload}
                autoUploadS3={false}
                name={`skp_internal${
                  values.skp_type?.toLowerCase() == 'subdistributor'
                    ? '_subdis'
                    : ''
                }${csvMode === 'autofill' ? '_autofill' : ''}`}
                validHeader={
                  values.skp_type?.toLowerCase() == 'subdistributor'
                    ? csvMode === 'autofill'
                      ? ['outlet_code', 'brand_id']
                      : ['outlet_code', 'brand_id', 'budget_sample', 'action']
                    : csvMode === 'autofill'
                    ? ['customer_code', 'brand_id']
                    : ['customer_code', 'brand_id', 'budget_sample', 'action']
                }
              />
            </>
          )}
        </Formik>
      ) : (
        false
      )}
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  row: { flexDirection: 'row' },
  rowForm: { width: isWeb ? 'calc(50% - 20px)' : '100%' },
  addOptions: {
    position: 'absolute',
    width: 150,
    top: 50,
    left: 0,
    zIndex: 1,
    elevation: 1,
    backgroundColor: Colors.white,
    borderRadius: 5,
    borderColor: Colors.grey0,
    borderWidth: 1,
    ...(!isWeb
      ? {
          position: 'absolute',
          top: 0,
          left: 100,
        }
      : {}),
  },
  addOptionsItem: {
    paddingVertical: 13,
    paddingHorizontal: 15,
    ...(!isWeb
      ? {
          borderBottomColor: Colors.grey0,
          borderBottomWidth: 1,
        }
      : {}),
  },
});

export default SkpInternalFormScreen;
